import { useEffect, useState } from 'react';

import { logout } from '@/shared/api';

import { getSessionTimeLeft } from './auth';

// setTimeout overflows past this and fires immediately.
const MAX_TIMEOUT_MS = 2_147_483_647;

type ExpiryCheck = 'startup' | 'timer' | 'visibility';

/**
 * Ends the session once the access token expires: on mount, when its `exp` passes, and when
 * a hidden tab comes back (background timers are throttled and stop while the machine sleeps).
 * Returns false once the session is over, while the login redirect is on its way.
 */
export const useSessionExpiry = (): boolean => {
  const [isActive, setIsActive] = useState(() => getSessionTimeLeft() !== 0);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = (trigger: ExpiryCheck) => {
      clearTimeout(timer);
      const timeLeft = getSessionTimeLeft();

      if (timeLeft === 0) {
        setIsActive(false);
        void logout({ reason: 'session_expired', context: { trigger } });
        return;
      }

      // Re-read on every check: another tab may have logged out or signed in again.
      if (timeLeft !== null) {
        timer = setTimeout(() => check('timer'), Math.min(timeLeft, MAX_TIMEOUT_MS));
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check('visibility');
      }
    };

    check('startup');
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return isActive;
};
